import { createContext, useContext, useState, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Trash2, XCircle, Info } from "lucide-react";

const ConfirmContext = createContext(null);

const VARIANT_CONFIG = {
  destructive: { icon: Trash2, iconClass: "text-rose-400 bg-rose-500/10", confirmClass: "bg-rose-600 hover:bg-rose-500 text-white", confirmDefault: "Delete" },
  warning: { icon: AlertTriangle, iconClass: "text-amber-400 bg-amber-500/10", confirmClass: "bg-amber-600 hover:bg-amber-500 text-white", confirmDefault: "Confirm" },
  danger: { icon: XCircle, iconClass: "text-rose-400 bg-rose-500/10", confirmClass: "bg-rose-600 hover:bg-rose-500 text-white", confirmDefault: "Confirm" },
  info: { icon: Info, iconClass: "text-blue-400 bg-blue-500/10", confirmClass: "bg-blue-600 hover:bg-blue-500 text-white", confirmDefault: "OK" },
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, title: "", description: "", confirmText: "", cancelText: "Cancel", variant: "warning" });
  const resolveRef = useRef(null);

  const confirm = useCallback(({ title = "Are you sure?", description = "", confirmText, cancelText = "Cancel", variant = "warning" } = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, title, description, confirmText: confirmText || VARIANT_CONFIG[variant]?.confirmDefault || "Confirm", cancelText, variant });
    });
  }, []);

  const handleClose = useCallback((result) => {
    setState(prev => ({ ...prev, open: false }));
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
  }, []);

  const cfg = VARIANT_CONFIG[state.variant] || VARIANT_CONFIG.warning;
  const IconComponent = cfg.icon;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={state.open} onOpenChange={(open) => { if (!open) handleClose(false); }}>
        <DialogContent className="bg-slate-900 border-slate-800 sm:max-w-[420px]" data-testid="confirm-dialog">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${cfg.iconClass}`}>
                <IconComponent className="w-4.5 h-4.5" />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-base font-semibold text-slate-100" data-testid="confirm-dialog-title">{state.title}</DialogTitle>
                {state.description && (
                  <DialogDescription className="text-sm text-slate-400 mt-1.5 whitespace-pre-line" data-testid="confirm-dialog-description">
                    {state.description}
                  </DialogDescription>
                )}
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" onClick={() => handleClose(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="confirm-dialog-cancel">
              {state.cancelText}
            </Button>
            <Button onClick={() => handleClose(true)} className={cfg.confirmClass} data-testid="confirm-dialog-confirm">
              {state.confirmText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}
